/* eslint-disable react-hooks/exhaustive-deps */
import parse from "html-react-parser";
import PropTypes from "prop-types";
import { useEffect, useState, useMemo, useRef } from "react";
import { AntSwitch } from "components/AntSwitch";
import { scrollToTop } from "utils/window";
import {
  ContainerView,
  ContainerRow,
  ContainerV2,
  ContainerViewStake,
} from "../../../components/Container";
import { Form } from "antd";
import { useApp } from "../../../contexts";
import { InfoTooltipIcon } from "../../Home/styles";
import { Tooltip } from "antd";
import moment from "moment";
import {
  errorView,
  formatCurrency,
  getUserStakeInfo,
  stakeRequest,
  getStakeOtp,
  stakeRequestConfirm,
  requestReStake,
  confirmReStake,
  getStakeTransactionDetail,
} from "../../../services/app";
import produce from "immer";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TitleWithBackWallet } from "../../../components/Layout";
import { WithdrawFormContainer } from "../styles";
import {
  Body,
  FormButton2,
  WalletConvertInfo,
  WalletConvertInfoItem,
  WalletConvertInfoItemTitle,
  WalletConvertInfoItemValue,
  FormButtonCancel3,
} from "./styles";

import { FormInputNumberRaw, FormItemRow } from "../../../components/Form";
import { AIAIcon } from "../../../components/Icons/AIAIcon";
import { error } from "../../../Notification";
import {
  RoundNumberOfDigitSGT,
  capitalizeText,
  formatNumber,
} from "../../../utils/helper";
import { PolygonIcon } from "../../../components/Icons/PolygonIcon";
import { useWindowSize } from "../../../hook/useWindowSize";
import { useVerificationMethod } from "../../../components/ModalVerificationMethod/useVerificationMethod";
import { calculateInterestAmount } from "../../../utils/helper";
import { StakeSuccessScreen } from "./StakeSuccessScreen";
import { useGasFee } from "contexts/gasFee";
import { AutoSubscribeConfirmModal } from "./AutoSubscribeConfirmModal";

const SNAPSHOT_LINK = "https://snapshot.box/#/s:suzuverse-dao.eth";

const HorizontalLine = ({ className = "" }) => (
  <div
    className={`bg-[#0000001A] w-full h-px${className ? ` ${className}` : ""}`}
  ></div>
);

export default function StakeSGTComponent({ type }) {
  const isStake = useMemo(() => type === "stake", [type]);
  const { T, loading, setLoading, user, getCurrency } = useApp();
  const title = isStake ? T("Stake Request") : T("Re-Stake Request");
  const [form] = Form.useForm();
  const formValuesRef = useRef({});
  const formAmount = Form.useWatch("amount", form);
  const size = useWindowSize();
  const isMobile = size.width < 768;
  const navigate = useNavigate();
  const [showAutoSubscribeModal, setShowAutoSubscribeModal] = useState(false);
  const watchedAmount = Form.useWatch("amount", form);
  const watchedDuration = Form.useWatch("duration", form);
  const watchedAutoSubscribe = Form.useWatch("auto_subscribe", form);

  const [searchParams, setSearchParams] = useSearchParams();
  const [stepSuccess, setStepSuccess] = useState(false);
  const { gasFee, nativeBalance, stakePlans, sgtBalance, energyConfig } =
    useGasFee();
  const [available, setAvailable] = useState(false);
  const [accountBalance, setAccountBalance] = useState({});
  const [stakeTx, setStakeTx] = useState(null);
  const [stakeData, setStakeData] = useState(null);
  const verification = useVerificationMethod();

  useEffect(() => {
    if (searchParams.get("formData")) {
      const data = JSON.parse(decodeURIComponent(searchParams.get("formData")));
      const formattedData = {
        ...data,
        // stake amount must be an integer
        amount: Math.floor(data.amount),
      };
      form.setFieldValue("amount", formattedData.amount);
      form.setFieldValue("duration", formattedData.duration);
      form.setFieldValue("auto_subscribe", formattedData.auto_subscribe);
      onUpdateData();
    }
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get("stakeTx")) {
      setStakeTx(searchParams.get("stakeTx"));
    }
  }, [searchParams]);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isDisabled, setIsDisabled] = useState(false);
  const [errors, setErrors] = useState([]);
  const [selectedDuration, setSelectedDuration] = useState({});

  // Fetch stake transaction data when stakeRecordId is in searchParams
  useEffect(() => {
    const fetchStakeTransaction = async () => {
      const stakeRecordId = searchParams.get("stakeRecordId");
      if (stakeRecordId && !isStake) {
        setLoading(true);
        const res = await getStakeTransactionDetail(stakeRecordId);
        if (res.status === 200 && res?.data) {
          const record = res.data;
          setStakeData(record);
          form.setFieldValue("amount", record.claimableAmount);
          form.setFieldValue("duration", record.duration);
          onUpdateData();
        } else {
          error(T("Failed to load stake transaction data"));
          console.error("Error fetching stake transaction:", res.data);
        }
        setLoading(false);
      }
    };

    fetchStakeTransaction();
  }, [isStake]);

  const stakeOptions = useMemo(() => {
    const stakeDurationOptions = stakePlans ?? [];
    const convertToYear = (duration, unit) => {
      const unitsInYear = {
        day: 365,
        week: 52,
        month: 12,
        year: 1,
      };
      const year = duration / (unitsInYear[unit] || 1);
      return Number(year.toFixed(2));
    };
    const formatData = (item) => ({
      value: item.id,
      label: item?.plan_name,
      year: convertToYear(+item?.duration, item?.duration_unit),
      duration: +item?.duration,
      durationUnit: `${item?.duration_unit}${+item?.duration > 1 ? "s" : ""}`,
      interestRateId: item?.interest_rate_id,
      interestRate: +item?.interest_rate,
      votingPowerInterestRate: +item?.voting_rate,
      interestRateUpdateDatetime: item?.interest_rate_update_datetime,
    });

    if (!isStake && stakeData) {
      // Use stakeData to filter duration options
      // Normalize durationUnit for comparison (handle both singular and plural)
      const normalizeUnit = (unit) => {
        if (!unit) return "";
        const baseUnit = unit.replace(/s$/, ""); // Remove trailing 's'
        return baseUnit;
      };
      const stakeDataUnitBase = normalizeUnit(stakeData.durationUnit);

      const filteredStakeDurationOptions =
        stakeDurationOptions
          .filter((item) => {
            const itemUnitBase = normalizeUnit(item.duration_unit);
            return (
              Number(item?.duration) === Number(stakeData.duration) &&
              itemUnitBase === stakeDataUnitBase
            );
          })
          .map(formatData) || [];
      
      if (filteredStakeDurationOptions.length === 0) {
        return [{
          value: '',
          duration: stakeData.duration,
          durationUnit: `${stakeData.durationUnit}${+stakeData.duration > 1 ? "s" : ""}`,
          interestRate: stakeData.interestRate,
        }];
      }

      return filteredStakeDurationOptions;
    }
    return stakeDurationOptions.map(formatData) || [];
  }, [stakePlans, stakeData, isStake]);

  useEffect(() => {
    if (stakeOptions.length > 0) {
      const durationOption = stakeOptions.find((item) => {
        return item.value === watchedDuration;
      });
      if (durationOption) {
        setSelectedDuration(durationOption);
        form.setFieldValue("duration", durationOption.value);
        onUpdateData();
      }
    }
  }, [watchedDuration, stakeOptions]);

  useEffect(() => {
    if (!isStake && stakeOptions.length > 0) {
      form.setFieldValue("duration", stakeOptions[0].value);
      onUpdateData();
    }
  }, [stakeOptions, isStake]);

  // Set selectedDuration from stakeData when available
  useEffect(() => {
    if (!isStake && stakeData && stakeOptions.length > 0) {
      // Normalize durationUnit for comparison
      const normalizeUnit = (unit) => {
        if (!unit) return "";
        return unit.replace(/s$/, ""); // Remove trailing 's'
      };
      const stakeDataUnitBase = normalizeUnit(stakeData.durationUnit);

      const durationOption = stakeOptions.find((item) => {
        const itemUnitBase = normalizeUnit(item.durationUnit);
        return (
          item.duration === stakeData.duration &&
          itemUnitBase === stakeDataUnitBase
        );
      });
      if (durationOption) {
        setSelectedDuration(durationOption);
        form.setFieldValue("duration", durationOption.value);
        onUpdateData();
      } else if (stakeOptions.length > 0) {
        // If exact match not found, use the first filtered option
        // This should rarely happen since we filter stakeOptions based on stakeData
        setSelectedDuration(stakeOptions[0]);
        form.setFieldValue("duration", stakeOptions[0].value);
        onUpdateData();
      }
    }
  }, [stakeData, stakeOptions, isStake]);

  const loadData = async () => {
    const res = await getUserStakeInfo();
    const data = res?.data;
    setAccountBalance(data);
  };

  const onSubmitForm = async (values) => {
    setLoading(true);
    try {
      let result;
      if (isStake) {
        result = await stakeRequest({
          ...values,
          auto_subscribe: undefined,
          autoSubscribe: values.auto_subscribe ? 1 : 0,
        });
      } else {
        if (!stakeData?.stakeRecordId) {
          error(T("Stake record not found"));
          return;
        }
        const stakeRecordIdNumber = Number(stakeData.stakeRecordId);
        if (isNaN(stakeRecordIdNumber)) {
          error(T("Invalid stake record ID"));
          return;
        }
        result = await requestReStake({
          stakeRecordId: stakeRecordIdNumber,
          amount: values.amount,
          autoSubscribe: values.auto_subscribe ? 1 : 0,
        });
      }
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("stakeTx", result?.data?.txId);
          return newParams;
        },
        {
          replace: true,
        }
      );
      formValuesRef.current = values;
      scrollToTop();
    } catch (err) {
      error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const maxAmount = useMemo(() => {
    if (isStake && sgtBalance && accountBalance) {
      return Math.min(sgtBalance || 0, accountBalance?.availableBalance || 0);
    }
    if (!isStake && stakeData) {
      return stakeData.claimableAmount;
    }
    return 0;
  }, [accountBalance, sgtBalance, stakeData, isStake]);

  const energyInterestRate = useMemo(() => {
    const val = energyConfig?.energyInterestRate;
    return val ? +val : 0;
  }, [energyConfig, stakePlans]);

  const rewardAmount = useMemo(() => {
    const val = calculateInterestAmount(
      watchedAmount ?? formValuesRef.current.amount ?? 0,
      selectedDuration?.interestRate
    );
    return val;
  }, [watchedAmount, selectedDuration?.interestRate]);

  const votingPower = useMemo(() => {
    const val = Math.floor(
      (+(watchedAmount ?? formValuesRef.current.amount ?? 0) *
        +(selectedDuration?.votingPowerInterestRate ?? 0)) /
        100
    );
    return val;
  }, [watchedAmount, selectedDuration?.votingPowerInterestRate]);

  const energyEarnAmount = useMemo(() => {
    const val = (+(rewardAmount ?? 0) * +(energyInterestRate ?? 0)) / 12;
    return val;
  }, [energyInterestRate, rewardAmount]);

  const claimableAmount = useMemo(() => {
    if (!isStake && stakeData) {
      // Calculate claimableAmount from stakeData if available
      if (stakeData.claimableAmount !== undefined) {
        return +stakeData.claimableAmount;
      }
      // Fallback: calculate from amount + interest
      if (stakeData.amount && stakeData.interestRate) {
        return (
          +stakeData.amount +
          calculateInterestAmount(stakeData.amount, stakeData.interestRate)
        );
      }
    }
    return 0;
  }, [stakeData, isStake]);

  const remainClaimableAmountDisplay = useMemo(() => {
    const remainClaimableAmount =
      claimableAmount - formAmount > 0 ? claimableAmount - formAmount : 0;
    return remainClaimableAmount
      ? `${formatNumber(remainClaimableAmount, 2)} SGT`
      : "0 SGT";
  }, [claimableAmount, formAmount]);

  const rewardAmountDisplay = useMemo(() => {
    return rewardAmount ? `${formatNumber(rewardAmount, 2)} SGT` : "0 SGT";
  }, [rewardAmount]);

  const votingPowerDisplay = useMemo(() => {
    return votingPower ? `${formatNumber(votingPower, 2)}` : "0";
  }, [votingPower]);

  const energyEarnAmountDisplay = useMemo(() => {
    return energyEarnAmount ? `${formatNumber(energyEarnAmount, 2)} e` : "0 e";
  }, [energyEarnAmount]);

  const tooltipContent = useMemo(() => {
    if (isStake) {
      return (
        <div>
          <div>
            {T(
              "Your maximum stake is determined as the lower value between the following:"
            )}
          </div>
          <ul className="list-disc pl-6">
            <li>
              {T(
                "Web3 Balance (SGT currently in your web3 wallet): {amount} SGT"
              ).replace("{amount}", RoundNumberOfDigitSGT(sgtBalance || 0))}
            </li>
            <li>
              {T(
                "Eligible Amount (SGT allowed to stake based on your exercised SOT limit): {amount} SGT"
              ).replace(
                "{amount}",
                RoundNumberOfDigitSGT(accountBalance?.availableBalance || 0)
              )}
            </li>
          </ul>
        </div>
      );
    } else {
      const stakedAmount = stakeData?.amount || 0;
      const rewardAmount = stakeData?.claimableAmount
        ? stakeData.claimableAmount - stakedAmount
        : 0;
      return (
        <div>
          <div>{T("Your available amount for re-staking is the sum of:")}</div>
          <ul className="list-disc pl-6">
            <li>
              {T("Staked amount: {amount} SGT").replace(
                "{amount}",
                RoundNumberOfDigitSGT(stakedAmount)
              )}
            </li>
            <li>
              {T("Reward amount: {amount} SGT").replace(
                "{amount}",
                RoundNumberOfDigitSGT(rewardAmount)
              )}
            </li>
          </ul>
        </div>
      );
    }
  }, [isStake, sgtBalance, accountBalance, stakeData, T]);

  useEffect(() => {
    setIsDisabled(loading);
  }, [loading]);

  const handleToggleAutoSubscribe = (checked) => {
    if (checked) {
      setShowAutoSubscribeModal(true);
    } else {
      form.setFieldValue("auto_subscribe", false);
      onUpdateData();
    }
  };

  const handleAutoSubscribeCancel = () => {
    setShowAutoSubscribeModal(false);
    form.setFieldValue("auto_subscribe", false);
    onUpdateData();
  };

  const handleAutoSubscribeAgree = () => {
    setShowAutoSubscribeModal(false);
    form.setFieldValue("auto_subscribe", true);
    onUpdateData();
  };

  const onUpdateData = () => {
    const values = form.getFieldsValue();
    // TODO: check is it necessary to setFormValues
    // setFormValues(values);
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("formData", encodeURIComponent(JSON.stringify(values)));
        return newParams;
      },
      {
        replace: true,
      }
    );
    const isValidForm =
      values.amount >= 1 &&
      values.duration >= 0 &&
      (maxAmount > 0 ? values.amount <= maxAmount : true);
    setAvailable(isValidForm);
  };
  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadData()]);
    } catch (error) {
      setAccountBalance({});
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (searchParams.get("line_connected")) {
      onSubmit({
        chooseMethod: true,
      });
      setSearchParams(
        (searchParams) => {
          searchParams.delete("line_connected");
          return searchParams;
        },
        {
          replace: true,
        }
      );
    }
  }, [searchParams]);
  const onSubmit = async (options = {}) => {
    verification.verify({
      chooseMethod: options?.chooseMethod,
      secondaryActionText: T("Back"),
      actionText: T("Verify"),
      skipTwoFa: !user?.is_twofa,
      sendOtp: async (method) => {
        try {
          setLoading(true);
          const result = await getStakeOtp({
            txId: stakeTx,
            otp_method: method,
          });
          if (result?.data?.success) {
            return {
              success: true,
            };
          } else {
            if (result?.data?.error?.message === "tx_completed") {
              return {
                success: false,
                message: T("Transaction already completed"),
              };
            }

            setErrors(result?.data?.error?.validates || []);
            return {
              success: false,
              message:
                result?.data?.error?.message ||
                T("Failed to send OTP. Please try again later."),
            };
          }
        } catch (error) {
          console.error(error);
          return {
            success: false,
            message:
              error?.response?.data?.error?.message ||
              T("Failed to send OTP. Please try again later."),
          };
        } finally {
          setLoading(false);
        }
      },
      verifyOtp: async (method, otp) => {
        try {
          setLoading(true);
          let result;
          if (isStake) {
            result = await stakeRequestConfirm({
              stake_interest_rate_id: selectedDuration?.interestRateId,
              interest_rate_update_datetime:
                selectedDuration?.interestRateUpdateDatetime,
              txId: stakeTx,
              emailCode: otp,
            });
          } else {
            result = await confirmReStake({
              txId: stakeTx,
              emailCode: otp,
            });
          }
          if (result?.data?.success) {
            return {
              success: true,
            };
          } else {
            if (result?.data?.error?.message === "otp_invalid") {
              return {
                success: false,
                otpError: T("Verification Code is not correct."),
              };
            }
            if (result?.data?.error?.message === "Insufficient balance") {
              error(T("Insufficient balance"));
              return {
                success: false,
                otpError: T("Insufficient balance"),
              };
            }
            if (typeof result?.data?.error?.message === "string" && result?.data?.error?.message?.includes("missing revert data (action=\"estimateGas\"")) {
              error(T("Insufficient {token} balance to cover gas fee.").replace("{token}", "POL"));
              return {
                success: false,
                otpError: T("Insufficient {token} balance to cover gas fee.").replace("{token}", "POL"),
              };
            }
            error(
              T(
                result?.data?.error?.message ??
                  "Failed to create your request. Please try again later."
              )
            );
            return {
              success: false,
              message: T(
                "Failed to create your request. Please try again later."
              ),
            };
          }
        } catch (err) {
          console.error(err);
          if (err?.response?.data?.error?.message === "otp_invalid") {
            return {
              success: false,
              otpError: T("Verification Code is not correct."),
            };
          } else {
            error(
              T(
                err?.response?.data?.error?.message ??
                  "Failed to create your request. Please try again later."
              )
            );
            return {
              success: false,
              message: T(
                "Failed to create your request. Please try again later."
              ),
            };
          }
        } finally {
          setLoading(false);
        }
      },
      action: async (method, otp, twoFa) => {
        try {
          setLoading(true);
          let result;
          if (isStake) {
            result = await stakeRequestConfirm({
              stake_interest_rate_id: selectedDuration?.interestRateId,
              interest_rate_update_datetime:
                selectedDuration?.interestRateUpdateDatetime,
              txId: stakeTx,
              emailCode: !user?.is_twofa ? otp : undefined,
              twofaCode: user?.is_twofa ? twoFa : undefined,
            });
          } else {
            result = await confirmReStake({
              txId: stakeTx,
              emailCode: !user?.is_twofa ? otp : undefined,
              twofaCode: user?.is_twofa ? twoFa : undefined,
            });
          }
          if (result?.data?.success) {
            setStepSuccess(true);
            const start = new Date().getTime();
            const endDate = getEndDate(
              start,
              selectedDuration.duration,
              selectedDuration.durationUnit
            );
            setStartDate(start);
            setEndDate(endDate);
            scrollToTop();
            return {
              success: true,
            };
          } else {
            if (result?.data?.error?.message === "otp_invalid") {
              return {
                success: false,
                otpError: T("Verification Code is not correct."),
              };
            }
            if (result?.data?.error?.message === "2fa_invalid") {
              return {
                success: false,
                twoFaError: T("2FA Code invalid"),
              };
            }
            if (
              result?.data?.error?.message === "native_balance_not_enough" ||
              result?.data?.error?.message === "Insufficient balance" ||
              (typeof result?.data?.error?.message === "string" && result?.data?.error?.message?.includes("missing revert data (action=\"estimateGas\""))
            ) {
              error(
                T("Insufficient {token} balance to cover gas fee.").replace(
                  "{token}",
                  "POL"
                )
              );
              return {
                success: false,
                twoFaError: user?.is_twofa
                  ? T("Insufficient {token} balance to cover gas fee.").replace(
                      "{token}",
                      "POL"
                    )
                  : undefined,
                otpError: !user?.is_twofa
                  ? T("Insufficient {token} balance to cover gas fee.").replace(
                      "{token}",
                      "POL"
                    )
                  : undefined,
              };
            }
            error(
              T(
                result?.data?.error?.message ??
                  "Failed to create your request. Please try again later."
              )
            );
            return {
              success: false,
              twoFaError: user?.is_twofa
                ? T("Failed to create your request. Please try again later.")
                : undefined,
              otpError: !user?.is_twofa
                ? T("Failed to create your request. Please try again later.")
                : undefined,
            };
          }
        } catch (err) {
          console.error(err);
          if (err?.response?.data?.error?.message === "otp_invalid") {
            return {
              success: false,
              otpError: T("Verification Code is not correct."),
            };
          } else if (err?.response?.data?.error?.message === "2fa_invalid") {
            return {
              success: false,
              twoFaError: T("2FA Code invalid"),
            };
          } else if (
            err?.response?.data?.error?.message === "native_balance_not_enough"
          ) {
            error(
              T("Insufficient {token} balance to cover gas fee.").replace(
                "{token}",
                "POL"
              )
            );
            return {
              success: false,
              twoFaError: user?.is_twofa
                ? T("Insufficient {token} balance to cover gas fee.").replace(
                    "{token}",
                    "POL"
                  )
                : undefined,
              otpError: !user?.is_twofa
                ? T("Insufficient {token} balance to cover gas fee.").replace(
                    "{token}",
                    "POL"
                  )
                : undefined,
            };
          } else {
            error(
              T(
                err?.response?.data?.error?.message ??
                  "Failed to create your request. Please try again later."
              )
            );
            return {
              success: false,
              twoFaError: user?.is_twofa
                ? T("Failed to create your request. Please try again later.")
                : undefined,
              otpError: !user?.is_twofa
                ? T("Failed to create your request. Please try again later.")
                : undefined,
            };
          }
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const getEndDate = (start, duration, durationUnit) => {
    if (!start || !duration || !durationUnit) return null;
    const endDate = moment(start);
    switch (durationUnit) {
      case "day":
      case "days":
        endDate.add(+duration, "days");
        break;
      case "week":
      case "weeks":
        endDate.add(+duration, "weeks");
        break;
      case "month":
      case "months":
        endDate.add(+duration, "months");
        break;
      case "year":
      case "years":
        endDate.add(+duration, "years");
        break;
      default:
        console.error("Invalid duration unit:", durationUnit);
    }
    return endDate.unix() * 1000;
  };

  const navigateToStakeList = () => {
    navigate(`/stake-for-option`);
  };

  const handleBackClick = () => {
    if (stakeTx && !stepSuccess) {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("stakeTx");
          return newParams;
        },
        {
          replace: true,
        }
      );
      setStakeTx(null);
    } else {
      navigateToStakeList();
    }
  };

  const renderEnterAmountScreen = () => {
    return (
      <>
        <div className="flex flex-col gap-1">
          <div className="text-[15px] text-[#818181] leading-6">
            {T("Network")}
          </div>
          <div className="bg-[#F5F5F57A] lg:bg-[#F5F5F5] rounded-[10px] h-[50px] px-3 flex items-center justify-start gap-2 ">
            <PolygonIcon />
            <div className="text-black text-base">
              {T("Polygon Main Network")}
            </div>
          </div>
        </div>
        <div className="flex justify-start items-start lg:items-center gap-2">
          <div className="note-icon w-[20px] h-[20px] mt-2 lg:mt-0">
            <img
              style={{ width: "100%", height: "100%" }}
              src={"/assets/images/alert.svg"}
              alt=""
              className="object-contain"
            />
          </div>
          <div className="flex-1 text-[13px] text-black">
            {parse(T("withdraw.alert_stake_pol"))}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <div className="text-[15px] text-[#818181] leading-6">
            {T("From Address")}
          </div>
          <div className="bg-[#F5F5F57A] lg:bg-[#F5F5F5] rounded-[10px] h-[50px] px-3 flex items-center justify-start gap-2">
            <AIAIcon />
            <div className="text-black text-base">
              {T("AI Avatar Web3 Wallet Address")}
            </div>
          </div>
        </div>

        <div className="flex justify-start items-start lg:items-center gap-2">
          <div className="note-icon w-[20px] h-[20px] mt-2 lg:mt-0">
            <img
              style={{ width: "100%", height: "100%" }}
              src={"/assets/images/alert.svg"}
              alt=""
              className="object-contain"
            />
          </div>
          <div className="flex-1 text-[13px] text-black">
            {parse(
              T("sgt_stake_for_vote_desc").replace(
                "{snapshot_link}",
                SNAPSHOT_LINK
              )
            )}
          </div>
        </div>

        <Form
          form={form}
          onFinish={() => {
            onSubmitForm(form.getFieldsValue());
          }}
          onValuesChange={() => onUpdateData()}
        >
          <FormItemRow
            {...errorView(errors, "amount")}
            className="no-after option-token-row"
            name={"amount"}
            label={T("Amount")}
            rules={[
              { required: true, message: T("Amount is required!") },
              {
                validator: (_, value) => {
                  if (value && value < 1) {
                    return Promise.reject(
                      new Error(T("Minimum stake = 1 SGT"))
                    );
                  }
                  if (value && value > maxAmount) {
                    return Promise.reject(new Error(T("Insufficient balance")));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <FormInputNumberRaw
              onChange={() => {
                setErrors(
                  produce((draft) => {
                    draft.splice(
                      draft.findIndex((item) => item.key === "amount"),
                      1
                    );
                  })
                );
              }}
              onKeyPress={(e) => {
                if (!/[0-9]/.test(e.key)) {
                  e.preventDefault();
                }
              }}
              min={0}
              onAll={() => {
                form.setFieldValue("amount", parseFloat(maxAmount || 0));
                setErrors(
                  produce((draft) => {
                    draft.splice(
                      draft.findIndex((item) => item.key === "amount"),
                      1
                    );
                  })
                );
                form.validateFields(["amount"]);
                onUpdateData();
              }}
              addonAfter={"SGT"}
              placeholder={T("Min 1")}
              autoFocus
              extra={
                <div className="relative">
                  <div className="absolute flex justify-end items-center right-0 text-[10px] text-[#818181] bottom-[-20px]">
                    {T("Available amount: {amount}").replace(
                      "{amount}",
                      `${maxAmount || 0} SGT`
                    )}
                    <Tooltip
                      trigger={isMobile ? "click" : "hover"}
                      onClick={(e) => e.stopPropagation()}
                      className="cursor-pointer mx-[5px]"
                      color={"#EAF2FF"}
                      overlayClassName={"note-tooltip-10"}
                      title={tooltipContent}
                    >
                      <span>
                        <InfoTooltipIcon className={"w-[13px]"} />
                      </span>
                    </Tooltip>
                  </div>
                </div>
              }
            />
          </FormItemRow>
          {!isStake && (
            <div className="flex justify-start items-start lg:items-center gap-2 py-3 md:py-5">
              <div className="note-icon w-[20px] h-[20px] mt-2 lg:mt-0">
                <img
                  style={{ width: "100%", height: "100%" }}
                  src={"/assets/images/alert.svg"}
                  alt=""
                  className="object-contain"
                />
              </div>
              <div className="flex-1 text-[13px] text-black">
                {T("restake_amount_desc")}
              </div>
            </div>
          )}
          <FormItemRow
            {...errorView(errors, "duration")}
            className="no-after option-token-row"
            name={"duration"}
            label={T("Duration")}
            rules={isStake ? [{ required: true, message: T("Duration is required!") }] : []}
          >
            <div className="grid md:grid-cols-4 grid-cols-2 gap-1.5 md:gap-3 min-h-[74px]">
              {stakeOptions.map((item) => (
                <div
                  key={item.value}
                  className={`stake-duration ${
                    item.value === watchedDuration ? "active" : ""
                  }`}
                  onClick={() => {
                    form.setFieldValue("duration", item.value);
                    setSelectedDuration(item);
                    onUpdateData();
                  }}
                >
                  <button className="stake-duration-inner" type="button">
                    <span className="font-[600] text-[20px]">
                      {formatNumber(item.duration, 2)}
                    </span>
                    <span className="font-[600] text-[10px] relative -top-[7px] capitalize ml-1">
                      {capitalizeText(T(item.durationUnit))}
                    </span>
                    <div className="font-[400] text-[12px]">
                      {`${T("Interest")} ${formatNumber(
                        item.interestRate,
                        2
                      )}%`}
                    </div>
                  </button>
                </div>
              ))}
            </div>
          </FormItemRow>
          <FormItemRow
            {...errorView(errors, "auto_subscribe")}
            className="no-after option-token-row"
            name={"auto_subscribe"}
            label={T("Auto subscribe")}
            oneRow
          >
            <AntSwitch
              checked={watchedAutoSubscribe ?? false}
              onChange={handleToggleAutoSubscribe}
              checkedChildren={T("ON")}
              unCheckedChildren={T("OFF")}
            />
          </FormItemRow>
          <div className="mt-[-20px] text-[10px] text-suzu-gray-2 mb-5">
            {parse(T("auto_subscribe_desc"))}
          </div>

          <HorizontalLine className="mb-5" />

          <ContainerRow className="mb-[50px]">
            <WalletConvertInfo className="flex flex-col gap-3 md:gap-5 flex-auto">
              <div>
                {!isStake && (
                  <>
                    <WalletConvertInfoItem>
                      <WalletConvertInfoItemTitle>
                        {T("Remain Claimable Amount")}
                      </WalletConvertInfoItemTitle>
                      <WalletConvertInfoItemValue>
                        {remainClaimableAmountDisplay}
                      </WalletConvertInfoItemValue>
                    </WalletConvertInfoItem>
                    <WalletConvertInfoItem>
                      <WalletConvertInfoItemTitle>
                        {T("Re-stake Amount")}
                      </WalletConvertInfoItemTitle>
                      <WalletConvertInfoItemValue>
                        {formatNumber(
                          watchedAmount ?? formValuesRef.current.amount ?? 0,
                          2
                        )}{" "}
                        SGT
                      </WalletConvertInfoItemValue>
                    </WalletConvertInfoItem>
                  </>
                )}

                <WalletConvertInfoItem>
                  <WalletConvertInfoItemTitle>
                    {T("Interest Amount")}
                  </WalletConvertInfoItemTitle>
                  <WalletConvertInfoItemValue>
                    {rewardAmountDisplay}
                  </WalletConvertInfoItemValue>
                </WalletConvertInfoItem>
                <WalletConvertInfoItem>
                  <WalletConvertInfoItemTitle>
                    {T("Voting Power")}
                  </WalletConvertInfoItemTitle>
                  <WalletConvertInfoItemValue>
                    {votingPowerDisplay}
                  </WalletConvertInfoItemValue>
                </WalletConvertInfoItem>

                <WalletConvertInfoItem>
                  <WalletConvertInfoItemTitle>
                    {T("Est. Monthly Energy Earn")}
                    <Tooltip
                      trigger={isMobile ? "click" : "hover"}
                      onClick={(e) => e.stopPropagation()}
                      className="cursor-pointer mx-1"
                      color={"#EAF2FF"}
                      overlayClassName={"note-tooltip-10"}
                      title={T(
                        "Energy earned is calculated annually based on staking reward amount and distributed to user every month based on amount of dates containing in month"
                      )}
                    >
                      <span>
                        <InfoTooltipIcon className={"w-[13px]"} />
                      </span>
                    </Tooltip>
                  </WalletConvertInfoItemTitle>
                  <WalletConvertInfoItemValue>
                    {energyEarnAmountDisplay}
                  </WalletConvertInfoItemValue>
                </WalletConvertInfoItem>
              </div>
              <HorizontalLine />

              <div>
                <WalletConvertInfoItem>
                  <WalletConvertInfoItemTitle>
                    {T("Estimated gas fee")}
                  </WalletConvertInfoItemTitle>

                  <WalletConvertInfoItemValue>
                    {watchedAmount >= 1 && watchedDuration >= 0 ? (
                      <>
                        {formatCurrency(gasFee, null, getCurrency())} {" POL"}
                      </>
                    ) : (
                      <>0 POL</>
                    )}
                  </WalletConvertInfoItemValue>
                </WalletConvertInfoItem>
                <div className="bottom-[-6px] text-[10px] text-[#818181]">
                  {T("Available Balance:")}{" "}
                  {`${RoundNumberOfDigitSGT(nativeBalance, 8)} POL`}
                </div>

                {watchedAmount >= 1 &&
                  watchedDuration >= 0 &&
                  gasFee > nativeBalance && (
                    <div className="text-red-500 text-[14px] italic mt-[4px]">
                      {T("Insufficient gas fee")}
                    </div>
                  )}
              </div>
            </WalletConvertInfo>
          </ContainerRow>

          <div className="flex justify-center">
            <FormButton2
              disabled={!available}
              htmlType={"button"}
              onClick={() => {
                form.submit();
              }}
            >
              {T("Continue")}
            </FormButton2>
          </div>
        </Form>
      </>
    );
  };

  const renderEnterVerificationScreen = () => {
    return (
      <>
        <div className="lg:px-3 flex flex-col gap-4">
          <div className="row-item-sot">
            <div className="text-[#818181] font-semibold font-base ">
              {T("Network")}
            </div>
            <div className="border-b border-solid border-[#CCCCCC] text-black font-normal text-base font-inter pb-[10px] flex justify-start items-center gap-2">
              <PolygonIcon />
              <div className="text-black text-base">
                {T("Polygon Main Network")}
              </div>
            </div>
          </div>
          <div className="row-item-sot">
            <div className="text-[#818181] font-semibold font-base ">
              {T("From Address")}
            </div>
            <div className="border-b border-solid border-[#CCCCCC] text-black font-normal text-base font-inter pb-[10px] flex justify-start items-center gap-2">
              <AIAIcon />{" "}
              <span className="">{T("AI Avatar Web3 Wallet Address")}</span>
            </div>
          </div>

          <div className="row-item-sot">
            <div className="text-[#818181] font-semibold font-base ">
              {T("Stake Amount")}
            </div>
            <div className="border-b border-solid border-[#CCCCCC] text-black font-normal text-base font-inter pb-[10px] flex flex-wrap justify-start items-center gap-2">
              <img
                alt=""
                style={{ width: 24, height: 24 }}
                src={`/assets/images/otc/crypto/sgt.svg`}
              />
              <div className="text-black text-base">
                {`${watchedAmount ?? formValuesRef.current.amount ?? 0} SGT`}
              </div>
            </div>
          </div>

          <div className="row-item-sot">
            <div className="text-[#818181] font-semibold font-base ">
              {T("Duration")}
            </div>
            <div className="border-b border-solid border-[#CCCCCC] text-black font-normal text-base font-inter pb-[10px] flex flex-wrap justify-start items-center gap-[3px]">
              <span>
                {formatNumber(selectedDuration.duration, 2)}&nbsp;
                <span className="capitalize">
                  {capitalizeText(T(selectedDuration.durationUnit))}
                </span>
              </span>
              <span>{`(${T("Interest")} ${formatNumber(
                selectedDuration.interestRate,
                2
              )}%)`}</span>
            </div>
          </div>

          <div className="row-item-sot">
            <div className="text-[#818181] font-semibold font-base ">
              {T("Interest Amount")}
            </div>
            <div className="border-b border-solid border-[#CCCCCC] text-black font-normal text-base font-inter pb-[10px] flex flex-wrap justify-start items-center gap-[3px]">
              <span>{rewardAmountDisplay}</span>
            </div>
          </div>

          <div className="row-item-sot">
            <div className="text-[#818181] font-semibold font-base ">
              {T("Voting Power")}
            </div>
            <div className="border-b border-solid border-[#CCCCCC] text-black font-normal text-base font-inter pb-[10px] flex flex-wrap justify-start items-center gap-[3px]">
              <span>{votingPowerDisplay}</span>
            </div>
          </div>

          <div className="row-item-sot">
            <div className="text-[#818181] font-semibold font-base ">
              {T("Est. Monthly Energy Earn")}
            </div>
            <div className="border-b border-solid border-[#CCCCCC] text-black font-normal text-base font-inter pb-[10px] flex flex-wrap justify-start items-center gap-[3px]">
              <span>{energyEarnAmountDisplay}</span>
            </div>
          </div>

          <div className="row-item-sot">
            <div className="text-[#818181] font-semibold font-base ">
              {T("Gas Fee")}
            </div>
            <div className="text-black font-normal text-base font-inter pb-[10px] flex justify-start items-center gap-2">
              <PolygonIcon />
              <div className="text-black text-base">{`${gasFee} POL`}</div>
            </div>
          </div>
        </div>
        <HorizontalLine className="mb-[30px] mt-[10px]" />
        <div className="flex justify-center items-center mt-4 gap-3 lg:mt-8">
          <FormButtonCancel3 onClick={navigateToStakeList}>
            <span>{T("Cancel")}</span>
          </FormButtonCancel3>
          <FormButton2
            disabled={isDisabled || loading || verification.loading}
            className={"lg:ml-3 ml-0 !w-[137px]"}
            onClick={() => onSubmit()}
          >
            {T("Confirm")}
          </FormButton2>
        </div>
      </>
    );
  };

  return (
    <ContainerV2>
      <ContainerView className="no-height">
        <TitleWithBackWallet
          className=" mt-3"
          title={title}
          onClick={handleBackClick}
        />
      </ContainerView>
      {!stepSuccess ? (
        <ContainerViewStake>
          <WithdrawFormContainer>
            <Body className="form flex flex-col gap-3 md:gap-5">
              {!stakeTx || Object.keys(formValuesRef.current).length === 0
                ? renderEnterAmountScreen()
                : renderEnterVerificationScreen()}
            </Body>
          </WithdrawFormContainer>
        </ContainerViewStake>
      ) : (
        <StakeSuccessScreen
          T={T}
          formValues={formValuesRef.current ?? {}}
          selectedDuration={selectedDuration}
          rewardAmountDisplay={rewardAmountDisplay}
          votingPowerDisplay={votingPowerDisplay}
          energyEarnAmountDisplay={energyEarnAmountDisplay}
          startDate={startDate}
          endDate={endDate}
          onBackHome={() => navigate("/")}
          onCreateAnother={navigateToStakeList}
        />
      )}
      <AutoSubscribeConfirmModal
        open={showAutoSubscribeModal}
        onCancel={handleAutoSubscribeCancel}
        onAgree={handleAutoSubscribeAgree}
      />
    </ContainerV2>
  );
}

// PropTypes
StakeSGTComponent.propTypes = {
  type: PropTypes.oneOf(["stake", "re-stake"]).isRequired,
};

// Default Props
StakeSGTComponent.defaultProps = {
  type: "stake",
};
